/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Comandero — Food cost (PURE LOGIC)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * CC-6: this file is PURE `.ts`. It imports NO Lingui macros (`msg`/`t`) or UI code.
 * It takes numbers (cents / integer quantities) and returns numbers.
 *
 * Conventions:
 *   - `ingredient.cost` is the cost in CENTS per stock unit (g/ml/unit).
 *   - `recipe_item.qty` is the quantity of that ingredient per sold unit.
 *   - Recipe cost = Σ(qty × cost), in cents.
 *   - food-cost % = recipe cost / sale price (0..1; ×100 for display).
 */

export interface RecipeCostLine {
  /** Ingredient quantity per sold unit (integer ≥ 0). */
  qty: number;
  /** Ingredient cost in cents per stock unit. */
  cost: number;
}

/** Recipe cost = Σ(qty × cost), in cents. */
export function recipeCost(lines: RecipeCostLine[]): number {
  return lines.reduce(
    (acc, l) => acc + Math.max(0, Math.trunc(l.qty)) * Math.max(0, Math.trunc(l.cost)),
    0,
  );
}

/**
 * Food cost as a fraction of the sale price (0..1). Returns 0 if the price is
 * ≤ 0 (avoids division by zero / infinite percentages for products without a price).
 */
export function foodCostRatio(cost: number, salePrice: number): number {
  const price = Math.trunc(salePrice);
  if (price <= 0) {
    return 0;
  }
  return Math.max(0, cost) / price;
}

/** Food cost in rounded PERCENTAGE POINTS (e.g. 33 = 33%). */
export function foodCostPercent(cost: number, salePrice: number): number {
  return Math.round(foodCostRatio(cost, salePrice) * 100);
}

/** Unit gross margin = price − cost, in cents (may be negative). */
export function grossMargin(cost: number, salePrice: number): number {
  return Math.trunc(salePrice) - Math.max(0, cost);
}
